import { Link } from '@tanstack/react-router';
import { useEffect, useId, useRef, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { useT } from '../lib/i18n';

/**
 * The top of a page: a serif title, an optional count in grey ("Klanten 68"), one line of
 * explanation and the page's main action.
 */
export function TitleBlock({ title, count, intro, action, above }: { title: string; count?: number | null; intro?: string; action?: ReactNode; above?: ReactNode }) {
  return (
    <header className="relative flex flex-col gap-3 pt-1">
      {/* The lines stay inside the header, so they never run through the search or tabs below. */}
      {above && <div className="relative">{above}</div>}
      <div className="relative flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          <h1 className="page-title">
            {title}
            {count != null && <span className="ml-3 text-muted/45">{count}</span>}
          </h1>
          {intro && <p className="mt-3 max-w-prose text-[15px] text-muted">{intro}</p>}
        </div>
        {action && <div className="flex flex-wrap items-center gap-2">{action}</div>}
      </div>
    </header>
  );
}

export function PageHeader({ orgId, title, intro, action, count }: { orgId: string; title: string; intro: string; action?: ReactNode; count?: number | null }) {
  const t = useT();
  return (
    <TitleBlock
      title={title}
      count={count}
      intro={intro}
      action={action}
      above={
        <Link to="/s/$orgId/salon" params={{ orgId }} className="settings-back inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-ink">
          <span aria-hidden>←</span> {t('common.back')}
        </Link>
      }
    />
  );
}

export function Section({ title, intro, children, action }: { title: string; intro?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="flex flex-col gap-4 rounded-[var(--radius-card)] border border-line bg-surface p-5 shadow-[0_1px_2px_rgb(2_53_42/0.04)] md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-[16px] font-semibold">{title}</h2>
          {intro && <p className="mt-0.5 max-w-prose text-sm text-muted">{intro}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/** On/off switch with a label and an optional one-line explanation (design rule 5). */
export function Switch({
  checked,
  onChange,
  label,
  hint,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
  id?: string;
}) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <label htmlFor={inputId} className="font-medium">
          {label}
        </label>
        {hint && <p className="text-sm text-muted">{hint}</p>}
      </div>
      <button
        id={inputId}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 h-7 w-12 shrink-0 rounded-full transition-colors ${checked ? 'bg-frame' : 'bg-switch-off'}`}
      >
        <span className={`absolute left-0 top-[2px] size-6 rounded-full bg-surface shadow transition-transform ${checked ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
      </button>
    </div>
  );
}

export function Select({ label, hint, id, children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { label?: string; hint?: string; id: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={id} className="text-sm font-semibold">
          {label}
        </label>
      )}
      <select id={id} className="min-h-11 rounded-[var(--radius-control)] border border-line bg-surface px-3 text-base" {...props}>
        {children}
      </select>
      {hint && <p className="text-sm text-muted">{hint}</p>}
    </div>
  );
}

export function TextArea({ label, id, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; id: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold">
        {label}
      </label>
      <textarea id={id} rows={3} className="rounded-[var(--radius-control)] border border-line bg-surface px-3 py-2 text-base" {...props} />
    </div>
  );
}

/** "1 u 15 min" / "45 min" */
export function useFormatMinutes() {
  const t = useT();
  return (m: number) => {
    const h = Math.floor(m / 60);
    const rest = m % 60;
    if (h === 0) return `${m} ${t('common.minutes')}`;
    return rest === 0 ? `${h} ${t('common.hour')}` : `${h} ${t('common.hour')} ${rest} ${t('common.minutes')}`;
  };
}

export function DurationSelect({ id, label, value, onChange, min = 5, max = 300, allowZero = false, hint }: {
  id: string;
  label: string;
  value: number;
  onChange: (m: number) => void;
  min?: number;
  max?: number;
  allowZero?: boolean;
  hint?: string;
}) {
  const t = useT();
  const fmt = useFormatMinutes();
  const options = [...(allowZero ? [0] : []), ...Array.from({ length: (max - min) / 5 + 1 }, (_, i) => min + i * 5)];
  return (
    <Select id={id} label={label} hint={hint} value={value} onChange={(e) => onChange(Number(e.target.value))}>
      {options.map((m) => (
        <option key={m} value={m}>
          {m === 0 ? t('common.none') : fmt(m)}
        </option>
      ))}
    </Select>
  );
}

export const formatEuro = (cents: number) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(cents / 100);

/** Accepts "35", "35,50", "35.50", "€ 35" → cents. */
export function parseEuro(text: string): number | null {
  const clean = text.replace(/[€\s]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  if (clean === '') return null;
  const n = Number(clean);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

export function EuroField({ id, label, hint, value, onChange, hideLabel = false }: { id: string; label: string; hint?: string; value: string; onChange: (v: string) => void; hideLabel?: boolean }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'text-sm font-semibold'}>
        {label}
      </label>
      <div className="flex min-h-11 items-center rounded-[var(--radius-control)] border border-line bg-surface px-3 focus-within:border-accent">
        <span className="text-muted">€</span>
        <input id={id} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent px-2 text-base outline-none" />
      </div>
      {hint && <p className="text-sm text-muted">{hint}</p>}
    </div>
  );
}

/** Side panel on desktop, bottom sheet on phones. Escape or the backdrop closes it. */
/** `focus`: what gets focus on open; 'close' keeps the phone keyboard down when typing isn't the point. */
export function SidePanel({ open, title, onClose, children, footer, focus = 'first' }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; focus?: 'first' | 'close' }) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  // Keep the latest onClose without re-running the effect (which would steal focus on every render).
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    window.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>(focus === 'close' ? '[data-close]' : 'input, select, textarea, button')?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, focus]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Desktop keeps the calendar readable behind the panel; phones dim it under the sheet. */}
      <button aria-label={t('common.close')} className="absolute inset-0 bg-frame/40 md:bg-frame/20" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative mt-auto flex max-h-[92dvh] w-full flex-col rounded-t-[24px] bg-canvas shadow-[0_-10px_40px_rgb(2_53_42/0.18)] md:my-2.5 md:mr-2.5 md:mt-2.5 md:h-[calc(100%-20px)] md:max-h-none md:w-[500px] md:rounded-[var(--radius-panel)] md:shadow-[0_20px_60px_rgb(2_53_42/0.28)]"
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-4 md:px-6">
          <h2 className="display truncate text-[26px]">{title}</h2>
          <button data-close onClick={onClose} className="flex size-9 shrink-0 items-center justify-center rounded-full text-muted hover:bg-subtle hover:text-ink" aria-label={t('common.close')}>
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5 md:px-6">{children}</div>
        {footer && <div className="flex items-center gap-2 border-t border-line px-5 py-3" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>{footer}</div>}
      </div>
    </div>
  );
}

export function SavedNote({ show }: { show: boolean }) {
  const t = useT();
  return <span aria-live="polite" className={`text-sm text-accent-strong transition-opacity ${show ? 'opacity-100' : 'opacity-0'}`}>✓ {t('common.saved')}</span>;
}
